import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield } from 'lucide-react';
import {
  CONSENT_KEY,
  CONSENT_RESET_EVENT,
  grantAnalyticsConsent,
  denyAnalyticsConsent,
} from '@/utils/consent';

type ConsentStatus = 'accepted' | 'declined' | null;

export const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY) as ConsentStatus | null;
    if (stored === 'accepted' || stored === 'declined') {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }

    // Reset listener — Settings -> Cookie-Einstellungen ponovo otvori banner
    // bez page reload-a (audio se ne prekida).
    const onReset = () => setIsVisible(true);
    window.addEventListener(CONSENT_RESET_EVENT, onReset);
    return () => window.removeEventListener(CONSENT_RESET_EVENT, onReset);
  }, []);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    grantAnalyticsConsent();
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, 'declined');
    denyAnalyticsConsent();
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-[100] p-4 sm:p-6"
      >
        <div
          className="mx-auto max-w-2xl rounded-2xl border border-white/20 bg-primary-bg/95 backdrop-blur-xl shadow-2xl p-6 sm:p-8"
          style={{ boxShadow: '0 -4px 30px rgba(0,0,0,0.3)' }}
        >
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent-pink/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-accent-pink" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-white mb-2">
                {t('cookieConsent.title')}
              </h3>
              <p className="text-sm text-text-secondary mb-4">
                <Trans
                  i18nKey="cookieConsent.text"
                  components={{
                    1: <Link to="/datenschutz" className="text-accent-pink hover:underline" />,
                    2: <Link to="/impressum" className="text-accent-pink hover:underline" />,
                  }}
                />
              </p>
              {/* DSGVO-compliant: oba dugmeta jednako prominentna (isti padding,
                  font, dimenzije, vizuelna težina). Accept nosi brand boju da
                  ohrabri pozitivnu odluku, Decline ima jak border + shadow da
                  NIJE vizuelno marginalizovan. */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleAccept}
                  className="px-5 py-2.5 rounded-full font-medium text-white transition-all hover:scale-105 active:scale-95 min-w-[120px]"
                  style={{
                    background: 'linear-gradient(135deg, #E64A8E 0%, #6A1A9A 100%)',
                    boxShadow: '0 4px 15px rgba(230, 74, 142, 0.4)',
                  }}
                >
                  {t('cookieConsent.accept')}
                </button>
                <button
                  onClick={handleDecline}
                  className="px-5 py-2.5 rounded-full font-medium bg-white/10 text-white hover:bg-white/20 hover:scale-105 active:scale-95 transition-all border-2 border-white/40 min-w-[120px]"
                  style={{
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.25)',
                  }}
                >
                  {t('cookieConsent.decline')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
